<template>
  <controller>
    <main class="sgu-wizard-page">
      <header class="wizard-header">
        <div>
          <nav class="wizard-links" aria-label="Navegação do SGU">
            <router-link :to="hasEvaluationPermission ? '/appscripts/sgu/parametrizacao' : '/appscripts/sgu'">
              &larr; {{ hasEvaluationPermission ? 'Voltar aos requisitos' : 'Voltar ao dashboard' }}
            </router-link>
            <router-link v-if="isDirectorAssessment" to="/appscripts/sgu/relatorio">
              Visualizar relatório
            </router-link>
          </nav>
          <h1>{{ pageTitle }}</h1>
          <p>{{ pageDescription }}</p>
        </div>
        <div v-if="!isDirectorAssessment" class="wizard-context">
          <label>
            <span>Ciclo</span>
            <select v-model="cycleId" :disabled="loading || saving || savingDraft" @change="reloadStructure">
              <option v-for="cycle in cycles" :key="cycle.id" :value="cycle.id">{{ cycle.code }}</option>
            </select>
          </label>
          <label v-if="isCsc">
            <span>Área responsável</span>
            <select v-model="areaId" :disabled="loading || saving || savingDraft" @change="reloadStructure">
              <option v-for="area in areas" :key="area.id" :value="area.id">{{ area.name }}</option>
            </select>
          </label>
        </div>
      </header>

      <form-spinner v-if="loading" />
      <section v-else-if="errorMessage" class="wizard-message error" role="alert">
        <p>{{ errorMessage }}</p>
        <button type="button" @click="loadStructure">Tentar novamente</button>
      </section>

      <section v-if="stepLocked && !loading && !errorMessage" class="wizard-message locked" role="status">
        {{ unitScopedSubmission ? 'Esta unidade já foi finalizada.' : 'Esta avaliação já foi finalizada.' }} As respostas estão disponíveis somente para consulta.
      </section>

      <div v-if="!loading && !errorMessage && currentStep" class="wizard-layout">
        <aside class="units-sidebar">
          <div class="units-sidebar-heading">
            <div>
              <strong>{{ isCsc ? 'Unidades' : 'Perguntas' }}</strong>
              <small v-if="isCsc">{{ completedUnitsCount }} de {{ units.length }} concluídas</small>
              <small v-else>{{ completedAnswersCount }} de {{ requirements.length }} concluídas</small>
            </div>
            <span>{{ completedAnswersCount }}/{{ steps.length }}</span>
          </div>

          <nav v-if="!isCsc" class="units-list" aria-label="Navegação das perguntas da autoavaliação">
            <button
              v-for="(requirement, index) in requirements"
              :key="requirement.id"
              type="button"
              :class="['unit-navigation', requirementStatus(requirement).status, { active: currentStep.requirement.id === requirement.id }]"
              :disabled="saving || savingDraft"
              :aria-current="currentStep.requirement.id === requirement.id ? 'step' : undefined"
              @click="goToRequirement(requirement)"
            >
              <span class="unit-status-icon" aria-hidden="true">{{ requirementStatus(requirement).complete ? '✓' : index + 1 }}</span>
              <span><strong>{{ requirement.name }}</strong><small>{{ requirement.area || 'Sem área responsável' }}</small></span>
            </button>
          </nav>

          <div v-else class="units-list">
            <button
              v-for="unit in units"
              :key="unit.id"
              type="button"
              :class="['unit-navigation', unitStatus(unit).status, { active: currentStep.unit.id === unit.id }]"
              :disabled="saving || savingDraft"
              @click="goToUnit(unit)"
            >
              <span class="unit-status-icon" aria-hidden="true">{{ unitStatus(unit).complete ? '✓' : '!' }}</span>
              <span><strong>{{ unit.code }}</strong><small>{{ unitStatus(unit).completed }}/{{ requirements.length }} respostas</small></span>
            </button>
          </div>

          <div v-if="!stepLocked" class="draft-status" :class="{ error: draftError }" role="status" aria-live="polite">
            <strong>{{ draftLabel }}</strong>
            <small>{{ draftStatus }}</small>
          </div>
        </aside>

        <section class="wizard-card">
        <div class="wizard-progress-copy">
          <span>{{ completedAnswersCount }} de {{ steps.length }} respostas concluídas</span>
          <strong>{{ progress }}%</strong>
        </div>
        <div class="wizard-progress"><span :style="{ width: `${progress}%` }" /></div>

        <div class="wizard-step-heading">
          <span class="unit-badge">{{ currentStep.unit.code }}</span>
          <div>
            <small>{{ currentStep.requirement.area || 'Sem área responsável' }} · Peso {{ formatWeight(currentStep.requirement.weight) }}</small>
            <h2>{{ currentStep.requirement.name }}</h2>
            <span class="question-position">Pergunta {{ currentRequirementIndex + 1 }} de {{ requirements.length }} desta unidade</span>
          </div>
        </div>

        <div class="layer-reference" role="radiogroup" aria-label="Selecione a camada atingida">
          <button
            v-for="layer in currentLayers"
            :key="layer.value"
            type="button"
            role="radio"
            :aria-checked="currentAnswer.layer === layer.value"
            :class="['layer-option', layerClass(layer.value), { selected: currentAnswer.layer === layer.value }]"
            :disabled="stepLocked || saving || savingDraft"
            @click="selectLayer(layer.value)"
          >
            <strong>{{ layer.label }}</strong>
            <p>{{ layerDescription(layer.value) }}</p>
          </button>
        </div>

        <div class="wizard-fields">
          <label v-if="!isCsc && currentStep.requirement.requiresSelfAssessmentAttachment && currentAnswer.layer !== 'not_applicable'" class="attachment-field">
            <span>Anexos <b>*</b></span>
            <input
              :key="currentKey"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              :disabled="stepLocked || saving || savingDraft"
              @change="selectFiles"
            >
            <small>Obrigatório para este requisito. Até 10 arquivos de 10 MB.</small>
          </label>
          <div v-if="currentAnswer.attachments.length || currentAnswer.files.length" class="attachment-list">
            <button
              v-for="attachment in currentAnswer.attachments"
              :key="`saved-${attachment.id}`"
              type="button"
              @click="downloadAttachment(attachment)"
            >
              {{ attachment.name }}
            </button>
            <span v-for="file in currentAnswer.files" :key="`${file.name}-${file.size}`">{{ file.name }}</span>
          </div>

          <label v-if="isCsc">
            <span>Justificativa <b v-if="requiresJustification">*</b></span>
            <textarea
              v-model.trim="currentAnswer.justification"
              rows="4"
              maxlength="5000"
              :disabled="stepLocked || saving || savingDraft"
              :placeholder="requiresJustification ? 'Obrigatória para a camada selecionada' : 'Opcional para Alta Performance'"
              @input="clearStepError"
            />
          </label>

          <label v-if="!isDirectorAssessment">
            <span>Link da evidência</span>
            <input
              v-model.trim="currentAnswer.evidenceLink"
              type="url"
              maxlength="2048"
              :disabled="stepLocked || saving || savingDraft"
              placeholder="https://..."
              @input="clearStepError"
            >
          </label>
        </div>

        <p v-if="stepError" class="step-error" role="alert">{{ stepError }}</p>

        <footer class="wizard-footer">
          <button
            v-if="!stepLocked"
            type="button"
            class="draft"
            :disabled="saving || savingDraft || !hasDraftAnswers"
            @click="saveDraft"
          >
            {{ savingDraft ? 'Salvando rascunho...' : 'Salvar rascunho' }}
          </button>
          <button type="button" class="secondary" :disabled="currentIndex === 0 || saving || savingDraft" @click="previousStep">
            Anterior
          </button>
          <button v-if="!isLastSubmissionStep" type="button" class="primary" :disabled="saving || savingDraft" @click="nextStep">
            Próxima etapa
          </button>
          <button v-else type="button" class="primary" :disabled="saving || savingDraft || stepLocked" @click="submitEvaluation">
            {{ stepLocked ? 'Avaliação finalizada' : saving ? 'Salvando...' : unitScopedSubmission ? 'Salvar unidade e finalizar' : 'Salvar tudo e finalizar' }}
          </button>
        </footer>
        </section>
      </div>

      <section v-else-if="!loading && !errorMessage" class="wizard-message">Nenhum requisito disponível para este ciclo.</section>
    </main>
  </controller>
</template>

<script setup>
/* global defineProps */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import FormSpinner from '@/components/utilities/FormSpinner.vue'
import UserService from '@/services/user.service'
import ImportedModulesService from '../services/importedModules.service'

const props = defineProps({
  mode: {
    type: String,
    default: 'director'
  }
})
const router = useRouter()
const cycles = ref([])
const areas = ref([])
const units = ref([])
const requirements = ref([])
const layers = ref([])
const answers = ref({})
const cycleId = ref('')
const areaId = ref('')
const currentIndex = ref(0)
const loading = ref(true)
const saving = ref(false)
const savingDraft = ref(false)
const errorMessage = ref('')
const stepError = ref('')
const draftSavedAt = ref('')
const draftSource = ref('')
const draftError = ref('')
const draftReady = ref(false)
const locked = ref(false)
const submissionScope = ref('area')
const submittedUnitIds = ref([])
let draftTimer = null
let pendingDraft = null

const steps = computed(() => units.value.flatMap(unit => requirements.value.map(requirement => ({ unit, requirement }))))
const isCsc = computed(() => props.mode === 'csc')
const hasDirectorPermission = UserService.filterPermissions('preencherAvaliacaoSgu')
const hasCscPermission = UserService.filterPermissions('preencherAvaliacaoSguCsc')
const hasEvaluationPermission = hasDirectorPermission || hasCscPermission
const isDirectorAssessment = computed(() => !isCsc.value && hasDirectorPermission)
const pageTitle = computed(() => isCsc.value ? 'Avaliação CSC do SGU' : 'Autoavaliação SGU')
const pageDescription = computed(() => isCsc.value
  ? (unitScopedSubmission.value
      ? 'Preencha e finalize uma unidade por vez durante cada visita.'
      : 'Preencha os requisitos da área responsável para todas as unidades escolares.')
  : 'Preencha todos os requisitos da sua unidade escolar.')
const currentStep = computed(() => steps.value[currentIndex.value] || null)
const currentKey = computed(() => currentStep.value ? answerKey(currentStep.value.unit.id, currentStep.value.requirement.id) : '')
const currentAnswer = computed(() => answers.value[currentKey.value] || emptyAnswer())
const unitScopedSubmission = computed(() => isCsc.value && submissionScope.value === 'unit')
const stepLocked = computed(() => locked.value || (unitScopedSubmission.value && submittedUnitIds.value.includes(Number(currentStep.value?.unit?.id))))
const submissionSteps = computed(() => unitScopedSubmission.value && currentStep.value
  ? steps.value.filter(step => step.unit.id === currentStep.value.unit.id)
  : steps.value)
const completedAnswersCount = computed(() => steps.value.filter(step => isAnswerComplete(
  answers.value[answerKey(step.unit.id, step.requirement.id)],
  step.requirement
)).length)
const completedUnitsCount = computed(() => units.value.filter(unit => unitStatus(unit).complete).length)
const progress = computed(() => steps.value.length ? Math.round((completedAnswersCount.value / steps.value.length) * 100) : 0)
const isLastStep = computed(() => currentIndex.value === steps.value.length - 1)
const isLastSubmissionStep = computed(() => unitScopedSubmission.value
  ? currentRequirementIndex.value === requirements.value.length - 1
  : isLastStep.value)
const requiresJustification = computed(() => ['not_met', 'basic', 'sustainable'].includes(currentAnswer.value.layer))
const currentRequirementIndex = computed(() => requirements.value.length ? currentIndex.value % requirements.value.length : 0)
const draftAnswers = computed(() => submissionSteps.value
  .map(step => ({
    unitId: step.unit.id,
    requirementId: step.requirement.id,
    ...answers.value[answerKey(step.unit.id, step.requirement.id)]
  }))
  .filter(answer => Boolean(answer.layer)))
const hasDraftAnswers = computed(() => draftAnswers.value.length > 0)
const pendingFilesCount = computed(() => draftAnswers.value.reduce(
  (total, answer) => total + (answer.files?.length || 0),
  0
))
const currentLayers = computed(() => {
  const available = currentStep.value?.requirement?.availableLayers || []
  return available.length ? layers.value.filter(layer => available.includes(layer.value)) : layers.value
})
const draftLabel = computed(() => {
  if (draftError.value) return 'Rascunho não salvo'
  if (savingDraft.value) return 'Salvando rascunho'
  if (draftSource.value === 'server') return 'Rascunho salvo no SGU'
  if (draftSource.value === 'local') return 'Cópia local automática'
  return 'Rascunho disponível'
})
const draftStatus = computed(() => {
  if (draftError.value) return draftError.value
  if (savingDraft.value) return 'Aguarde enquanto suas respostas são enviadas.'
  if (!draftSavedAt.value) return 'Responda uma etapa e use “Salvar rascunho” para continuar depois.'

  const time = formatDraftTime(draftSavedAt.value)
  if (draftSource.value === 'server') return `Salvo no SGU às ${time}. Você pode continuar em outro dispositivo.`
  if (pendingFilesCount.value) {
    return `Cópia local às ${time}. ${pendingFilesCount.value} arquivo(s) aguardam “Salvar rascunho”.`
  }
  return `Cópia local às ${time}. Use “Salvar rascunho” para sincronizar.`
})

onMounted(loadStructure)
onBeforeUnmount(writePendingDraft)

watch(answers, scheduleDraftSave, { deep: true })

async function loadStructure () {
  draftReady.value = false
  loading.value = true
  errorMessage.value = ''
  stepError.value = ''

  try {
    const getStructure = isCsc.value
      ? ImportedModulesService.getSguCscStructure.bind(ImportedModulesService)
      : ImportedModulesService.getSguStructure.bind(ImportedModulesService)
    const response = await getStructure({
      cycleId: cycleId.value || undefined,
      areaId: areaId.value || undefined
    })
    const data = response.data || {}
    cycles.value = data.cycles || []
    areas.value = data.areas || []
    units.value = data.units || []
    requirements.value = data.requirements || []
    layers.value = data.layers || []
    cycleId.value = data.selectedCycleId
    areaId.value = data.selectedAreaId || ''
    locked.value = Boolean(data.locked)
    submissionScope.value = data.submissionScope || 'area'
    submittedUnitIds.value = (data.submittedUnitIds || []).map(Number)
    currentIndex.value = 0
    hydrateAnswers(data.answers || [], data.draftSavedAt || '')
    await nextTick()
    draftReady.value = true
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Não foi possível carregar a estrutura do SGU.'
  } finally {
    loading.value = false
  }
}

function reloadStructure () {
  writePendingDraft()
  draftReady.value = false
  answers.value = {}
  loadStructure()
}

function hydrateAnswers (savedAnswers, serverDraftSavedAt) {
  const saved = new Map(savedAnswers.map(answer => [answerKey(answer.unitId, answer.requirementId), answer]))
  const localDraft = locked.value ? null : readDraft()
  const draft = localDraft && isNewerDraft(localDraft.savedAt, serverDraftSavedAt) ? localDraft : null
  if (localDraft && !draft) removeLocalDraft()
  answers.value = steps.value.reduce((result, step) => {
    const key = answerKey(step.unit.id, step.requirement.id)
    const unitSubmitted = unitScopedSubmission.value && submittedUnitIds.value.includes(Number(step.unit.id))
    result[key] = { ...emptyAnswer(), ...(saved.get(key) || {}), ...(!unitSubmitted ? draft?.answers?.[key] : {}) }
    return result
  }, {})
  draftSavedAt.value = draft?.savedAt || serverDraftSavedAt || ''
  draftSource.value = draft ? 'local' : (serverDraftSavedAt ? 'server' : '')
}

function emptyAnswer () {
  return { layer: '', justification: '', evidenceLink: '', attachments: [], files: [] }
}

function answerKey (unitId, requirementId) {
  return `${unitId}:${requirementId}`
}

function validateCurrentStep () {
  const answer = currentAnswer.value
  if (!answer.layer) {
    stepError.value = 'Selecione a camada antes de continuar.'
    return false
  }

  if (isCsc.value && ['not_met', 'basic', 'sustainable'].includes(answer.layer)) {
    if (!answer.justification) {
      stepError.value = 'A justificativa é obrigatória para Não Atende, Básica e Sustentável.'
      return false
    }
  }
  if (!isDirectorAssessment.value && answer.evidenceLink && !isValidHttpUrl(answer.evidenceLink)) {
    stepError.value = 'O link de evidência informado não é válido.'
    return false
  }

  if (!isCsc.value
    && answer.layer !== 'not_applicable'
    && currentStep.value.requirement.requiresSelfAssessmentAttachment
    && !answer.attachments.length
    && !answer.files.length) {
    stepError.value = 'Adicione ao menos um anexo para este requisito.'
    return false
  }

  stepError.value = ''
  return true
}

function isValidHttpUrl (value) {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol)
  } catch (_) {
    return false
  }
}

function nextStep () {
  if (!stepLocked.value && !validateCurrentStep()) return
  currentIndex.value++
}

function goToUnit (unit) {
  const unitStart = steps.value.findIndex(step => step.unit.id === unit.id)
  if (unitStart < 0) return

  const firstPendingOffset = requirements.value.findIndex(requirement => (
    !isAnswerComplete(answers.value[answerKey(unit.id, requirement.id)], requirement)
  ))

  currentIndex.value = unitStart + (firstPendingOffset >= 0 ? firstPendingOffset : 0)
  stepError.value = ''
  if (typeof window.scrollTo === 'function') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

function goToRequirement (requirement) {
  const requirementIndex = steps.value.findIndex(step => (
    step.unit.id === currentStep.value.unit.id && step.requirement.id === requirement.id
  ))
  if (requirementIndex < 0) return

  currentIndex.value = requirementIndex
  stepError.value = ''
  if (typeof window.scrollTo === 'function') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

function requirementStatus (requirement) {
  const answer = answers.value[answerKey(currentStep.value.unit.id, requirement.id)]
  const complete = isAnswerComplete(answer, requirement)

  return {
    complete,
    status: complete ? 'complete' : 'pending'
  }
}

function unitStatus (unit) {
  const completed = requirements.value.filter(requirement => (
    isAnswerComplete(answers.value[answerKey(unit.id, requirement.id)], requirement)
  )).length

  return {
    completed,
    complete: requirements.value.length > 0 && completed === requirements.value.length,
    status: requirements.value.length > 0 && completed === requirements.value.length ? 'complete' : 'pending'
  }
}

function previousStep () {
  stepError.value = ''
  currentIndex.value--
}

function clearStepError () {
  stepError.value = ''
}

async function saveDraft () {
  if (stepLocked.value || saving.value || savingDraft.value || !hasDraftAnswers.value) return

  savingDraft.value = true
  draftError.value = ''
  try {
    const saveEvaluationDraft = isCsc.value
      ? ImportedModulesService.saveSguCscDraft.bind(ImportedModulesService)
      : ImportedModulesService.saveSguDraft.bind(ImportedModulesService)
    const response = await saveEvaluationDraft({
      cycleId: Number(cycleId.value),
      areaId: Number(areaId.value),
      unitId: unitScopedSubmission.value ? Number(currentStep.value.unit.id) : undefined,
      answers: draftAnswers.value.map(answer => ({
        ...answer,
        justification: isCsc.value ? answer.justification : '',
        evidenceLink: isDirectorAssessment.value ? '' : answer.evidenceLink
      }))
    })
    const data = response.data || {}

    draftReady.value = false
    mergeServerDraft(data.answers || [])
    removeLocalDraft()
    draftSavedAt.value = data.draftSavedAt || new Date().toISOString()
    draftSource.value = 'server'
    await nextTick()
    draftReady.value = true
    window.toaster?.emit('open', {
      style: 'success',
      message: 'Rascunho do SGU salvo com sucesso.',
      floater: true
    })
  } catch (error) {
    draftError.value = firstValidationError(error) || error.response?.data?.message || 'Não foi possível salvar o rascunho no SGU.'
  } finally {
    savingDraft.value = false
    draftReady.value = !stepLocked.value
  }
}

function mergeServerDraft (savedAnswers) {
  savedAnswers.forEach(answer => {
    const key = answerKey(answer.unitId, answer.requirementId)
    if (!answers.value[key]) return

    answers.value[key] = {
      ...answers.value[key],
      ...answer,
      attachments: answer.attachments || answers.value[key].attachments || [],
      files: []
    }
  })
}

async function submitEvaluation () {
  if (stepLocked.value) return
  if (!validateCurrentStep()) return

  const firstInvalidStep = submissionSteps.value.find(step => !isAnswerComplete(
    answers.value[answerKey(step.unit.id, step.requirement.id)],
    step.requirement
  ))
  if (firstInvalidStep) {
    currentIndex.value = steps.value.findIndex(step => step.unit.id === firstInvalidStep.unit.id && step.requirement.id === firstInvalidStep.requirement.id)
    stepError.value = 'Revise esta etapa antes de finalizar a avaliação.'
    return
  }

  saving.value = true
  errorMessage.value = ''
  try {
    const saveEvaluation = isCsc.value
      ? ImportedModulesService.saveSguCscEvaluations.bind(ImportedModulesService)
      : ImportedModulesService.saveSguEvaluations.bind(ImportedModulesService)
    const submittedUnitId = unitScopedSubmission.value ? Number(currentStep.value.unit.id) : null
    await saveEvaluation({
      cycleId: Number(cycleId.value),
      areaId: Number(areaId.value),
      unitId: submittedUnitId || undefined,
      answers: submissionSteps.value.map(step => ({
        unitId: step.unit.id,
        requirementId: step.requirement.id,
        ...answers.value[answerKey(step.unit.id, step.requirement.id)],
        justification: isCsc.value ? answers.value[answerKey(step.unit.id, step.requirement.id)].justification : '',
        evidenceLink: isDirectorAssessment.value ? '' : answers.value[answerKey(step.unit.id, step.requirement.id)].evidenceLink
      }))
    })
    if (unitScopedSubmission.value) {
      // Keep local answers from other units; submitted units are hydrated only from the server.
      writePendingDraft()
      await loadStructure()
      const nextUnit = units.value.find(unit => !submittedUnitIds.value.includes(Number(unit.id)))
      if (nextUnit) goToUnit(nextUnit)
      window.toaster?.emit('open', { style: 'success', message: 'Avaliação da unidade finalizada com sucesso.', floater: true })
    } else {
      clearDraft()
      router.push('/appscripts/sgu')
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Não foi possível salvar a avaliação do SGU.'
  } finally {
    saving.value = false
  }
}

function isAnswerComplete (answer, requirement) {
  if (!answer?.layer) return false
  if (!isCsc.value && answer.layer !== 'not_applicable' && requirement?.requiresSelfAssessmentAttachment && !answer.attachments?.length && !answer.files?.length) return false
  if (answer.layer === 'not_applicable') return true
  if (isDirectorAssessment.value) return true
  if (answer.layer === 'not_met') return Boolean((!isCsc.value || answer.justification) && (!answer.evidenceLink || isValidHttpUrl(answer.evidenceLink)))
  if (!['basic', 'sustainable'].includes(answer.layer)) return !answer.evidenceLink || isValidHttpUrl(answer.evidenceLink)
  return Boolean((!isCsc.value || answer.justification) && (!answer.evidenceLink || isValidHttpUrl(answer.evidenceLink)))
}

function selectFiles (event) {
  if (stepLocked.value) return
  currentAnswer.value.files = Array.from(event.target.files || []).slice(0, 10)
  clearStepError()
}

async function downloadAttachment (attachment) {
  try {
    const response = await ImportedModulesService.downloadSguAttachment(attachment.id)
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.name
    link.click()
    URL.revokeObjectURL(url)
  } catch (_) {
    stepError.value = 'Não foi possível baixar o anexo.'
  }
}

function formatWeight (value) {
  return Number(value || 1).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

function selectLayer (layer) {
  if (stepLocked.value) return
  currentAnswer.value.layer = layer
  if (layer === 'not_applicable') {
    currentAnswer.value.justification = ''
    currentAnswer.value.evidenceLink = ''
    currentAnswer.value.files = []
  }
  clearStepError()
}

function layerDescription (layer) {
  if (layer === 'not_applicable') return 'Este requisito não se aplica à unidade avaliada.'
  if (layer === 'not_met') return 'O requisito não foi atendido.'
  if (layer === 'basic') return currentStep.value?.requirement?.basicDescription
  if (layer === 'sustainable') return currentStep.value?.requirement?.sustainableDescription
  return currentStep.value?.requirement?.highPerformanceDescription
}

function layerClass (layer) {
  if (layer === 'high_performance') return 'high'
  if (layer === 'not_applicable') return 'not-applicable'
  return layer.replace('_', '-')
}

function scheduleDraftSave () {
  if (!draftReady.value || stepLocked.value || !cycleId.value) return

  pendingDraft = {
    key: draftStorageKey(),
    value: {
      savedAt: new Date().toISOString(),
      answers: Object.fromEntries(Object.entries(answers.value).map(([key, answer]) => [key, {
        layer: answer.layer,
        justification: answer.justification,
        evidenceLink: isDirectorAssessment.value ? '' : answer.evidenceLink,
        attachments: answer.attachments || [],
        files: []
      }]))
    }
  }

  window.clearTimeout(draftTimer)
  draftTimer = window.setTimeout(writePendingDraft, 400)
}

function writePendingDraft () {
  if (!pendingDraft) return

  try {
    localStorage.setItem(pendingDraft.key, JSON.stringify(pendingDraft.value))
    draftSavedAt.value = pendingDraft.value.savedAt
    draftSource.value = 'local'
    draftError.value = ''
  } catch (_) {
    draftError.value = 'O navegador não permitiu guardar as alterações.'
  } finally {
    pendingDraft = null
    window.clearTimeout(draftTimer)
    draftTimer = null
  }
}

function readDraft () {
  try {
    const stored = localStorage.getItem(draftStorageKey())
    if (!stored) return null
    const draft = JSON.parse(stored)
    return draft && typeof draft.answers === 'object' ? draft : null
  } catch (_) {
    draftError.value = 'O rascunho anterior não pôde ser recuperado.'
    return null
  }
}

function clearDraft () {
  removeLocalDraft()
  draftSavedAt.value = ''
  draftSource.value = ''
}

function removeLocalDraft () {
  pendingDraft = null
  window.clearTimeout(draftTimer)
  draftTimer = null
  try {
    localStorage.removeItem(draftStorageKey())
  } catch (_) {
    // O rascunho persistido no SGU continua válido mesmo sem acesso ao armazenamento local.
  }
}

function draftStorageKey () {
  return `sgu:draft:${props.mode}:${currentUserIdentity()}:${cycleId.value}:${areaId.value}`
}

function currentUserIdentity () {
  try {
    const profile = JSON.parse(atob(localStorage.getItem('profile') || ''))
    return profile?.email || profile?.colaborador?.idPessoa || 'usuario'
  } catch (_) {
    return 'usuario'
  }
}

function isNewerDraft (localSavedAt, serverSavedAt) {
  if (!localSavedAt) return false
  if (!serverSavedAt) return true

  return new Date(localSavedAt).getTime() > new Date(serverSavedAt).getTime()
}

function firstValidationError (error) {
  const errors = error.response?.data?.errors
  return errors ? Object.values(errors).flat()[0] : ''
}

function formatDraftTime (value) {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}
</script>

<style scoped>
.sgu-wizard-page { margin: 0 auto; max-width: 1280px; padding: 20px 28px 40px; color: #1a1a2e; }
.wizard-header { align-items: flex-end; background: #c0001d; border-radius: 10px; color: #fff; display: flex; gap: 24px; justify-content: space-between; padding: 20px 24px; }
.wizard-header a { color: #ffd0d0; font-size: 11px; text-decoration: none; }
.wizard-links { display: flex; flex-wrap: wrap; gap: 14px; }
.wizard-links a:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
.wizard-header h1 { font-size: 21px; margin: 8px 0 3px; }
.wizard-header p { font-size: 12px; margin: 0; opacity: .9; }
.wizard-context { display: flex; gap: 10px; }
.wizard-context label { display: grid; gap: 4px; }
.wizard-context span { color: #ffd0d0; font-size: 10px; text-transform: uppercase; }
.wizard-context select { background: #fff; border: 0; border-radius: 6px; color: #333; height: 36px; min-width: 160px; padding: 0 10px; }
.wizard-layout { align-items: start; display: grid; gap: 16px; grid-template-columns: 220px minmax(0, 1fr); margin-top: 18px; }
.wizard-card { background: #fff; border: 1px solid #e8eaf0; border-radius: 10px; box-shadow: 0 2px 8px rgba(10, 18, 75, .06); padding: 24px; }
.units-sidebar { background: #fff; border: 1px solid #e8eaf0; border-radius: 10px; box-shadow: 0 2px 8px rgba(10, 18, 75, .06); overflow: hidden; position: sticky; top: 16px; }
.units-sidebar-heading { align-items: center; border-bottom: 1px solid #eceef3; display: flex; justify-content: space-between; padding: 14px; }
.units-sidebar-heading div { display: grid; gap: 3px; }
.units-sidebar-heading strong { font-size: 13px; }
.units-sidebar-heading small { color: #777; font-size: 10px; }
.units-sidebar-heading > span { background: #f1f2f5; border-radius: 999px; color: #555; font-size: 10px; font-weight: 700; padding: 5px 7px; }
.units-list { display: grid; max-height: calc(100vh - 280px); overflow-y: auto; padding: 7px; }
.unit-navigation { align-items: center; background: transparent; border: 1px solid transparent; border-radius: 7px; color: #333; cursor: pointer; display: grid; gap: 9px; grid-template-columns: 25px 1fr; min-height: 44px; padding: 8px; text-align: left; width: 100%; }
.unit-navigation:focus-visible { outline: 2px solid #c0001d; outline-offset: 2px; }
.unit-navigation:hover { background: #f6f7f9; }
.unit-navigation.active { background: #f5f6ff; border-color: #aeb4df; }
.unit-navigation > span:last-child { display: grid; gap: 2px; }
.unit-navigation strong { font-size: 12px; }
.unit-navigation small { color: #747986; font-size: 9px; }
.unit-status-icon { align-items: center; border-radius: 50%; color: #fff; display: flex; font-size: 11px; font-weight: 800; height: 22px; justify-content: center; width: 22px; }
.unit-navigation.complete .unit-status-icon { background: #27823f; }
.unit-navigation.pending .unit-status-icon { background: #c52b3d; }
.draft-status { background: #eef8f0; border-top: 1px solid #d8eadc; color: #246535; display: grid; gap: 2px; padding: 12px 14px; }
.draft-status.error { background: #fff1f2; border-color: #f2d3d7; color: #a60016; }
.wizard-message.locked { background: #fff8e6; border: 1px solid #eed89d; color: #6f5312; margin-top: 18px; }
.draft-status strong { font-size: 12px; }
.draft-status small { font-size: 12px; line-height: 1.35; }
.wizard-progress-copy { color: #6b7280; display: flex; font-size: 11px; justify-content: space-between; }
.wizard-progress { background: #eceef3; border-radius: 4px; height: 6px; margin: 8px 0 24px; overflow: hidden; }
.wizard-progress span { background: #c0001d; display: block; height: 100%; transition: width .2s ease; }
.wizard-step-heading { align-items: center; display: flex; gap: 14px; margin-bottom: 18px; }
.unit-badge { align-items: center; background: #09115b; border-radius: 8px; color: #fff; display: flex; font-size: 18px; font-weight: 700; height: 54px; justify-content: center; min-width: 68px; }
.wizard-step-heading small { color: #c0001d; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.wizard-step-heading h2 { font-size: 20px; margin: 4px 0 0; }
.question-position { color: #737986; display: block; font-size: 10px; margin-top: 5px; }
.layer-reference { display: grid; gap: 10px; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.layer-option { background: #fafafa; border: 2px solid transparent; border-radius: 8px; cursor: pointer; min-height: 116px; padding: 14px; text-align: left; }
.layer-option:hover { background: #f5f6f8; }
.layer-option:focus-visible { outline: 3px solid #09115b; outline-offset: 2px; }
.layer-option.selected { background: #fff7f7; border-color: #c0001d; }
.layer-option:disabled { cursor: not-allowed; opacity: .72; }
.layer-option.not-applicable { border-top-color: #8b909a; }
.layer-option.not-met { border-top-color: #b72f40; }
.layer-option.basic { border-top-color: #a86e00; }
.layer-option.sustainable { border-top-color: #26713c; }
.layer-option.high { border-top-color: #28629c; }
.layer-reference strong { color: #09115b; font-size: 12px; }
.layer-reference p { color: #555; font-size: 11px; line-height: 1.5; margin: 8px 0 0; }
.wizard-fields { display: grid; gap: 15px; margin-top: 22px; }
.wizard-fields label { display: grid; gap: 6px; }
.wizard-fields span { color: #444; font-size: 11px; font-weight: 600; }
.wizard-fields b { color: #c0001d; }
.wizard-fields select, .wizard-fields textarea, .wizard-fields input { border: 1px solid #d8dbe5; border-radius: 6px; color: #30313b; font: inherit; padding: 10px 12px; width: 100%; }
.wizard-fields select, .wizard-fields input { height: 40px; }
.wizard-fields textarea { resize: vertical; }
.attachment-field small { color: #737986; font-size: 10px; }
.attachment-list { display: flex; flex-wrap: wrap; gap: 8px; }
.attachment-list button, .attachment-list span { background: #f1f2f5; border: 0; border-radius: 999px; color: #444; font-size: 10px; padding: 7px 10px; }
.attachment-list button { color: #a5001a; cursor: pointer; text-decoration: underline; }
.step-error, .wizard-message.error { color: #a60016; }
.step-error { background: #fff0f0; border-radius: 6px; font-size: 11px; margin-top: 14px; padding: 10px 12px; }
.wizard-footer { display: flex; gap: 10px; justify-content: flex-end; margin-top: 22px; }
.wizard-footer button, .wizard-message button { border: 0; border-radius: 6px; cursor: pointer; font-weight: 600; min-height: 44px; min-width: 120px; padding: 10px 16px; }
.wizard-footer .primary, .wizard-message button { background: #c0001d; color: #fff; }
.wizard-footer .secondary { background: #eceef3; color: #333; }
.wizard-footer .draft { background: #fff; border: 1px solid #c0001d; color: #a5001a; margin-right: auto; }
.wizard-footer button:focus-visible, .wizard-message button:focus-visible { outline: 2px solid #c0001d; outline-offset: 2px; }
.wizard-footer button:disabled { cursor: not-allowed; opacity: .5; }
.wizard-message { background: #fff; border-radius: 10px; margin-top: 18px; padding: 36px; text-align: center; }
@media (max-width: 1100px) { .layer-reference { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 900px) { .wizard-header { align-items: stretch; flex-direction: column; } .wizard-context { grid-template-columns: 1fr; } .wizard-context { display: grid; } .wizard-layout { grid-template-columns: 1fr; } .units-sidebar { position: static; } .units-list { display: flex; max-height: none; overflow-x: auto; } .unit-navigation { flex: 0 0 125px; } nav.units-list .unit-navigation { flex-basis: 240px; } }
@media (max-width: 600px) { .layer-reference { grid-template-columns: 1fr; } }
@media (max-width: 600px) { .wizard-footer { flex-wrap: wrap; } .wizard-footer .draft { flex-basis: 100%; margin-right: 0; } .wizard-footer .primary, .wizard-footer .secondary { flex: 1; } }
</style>
